/**
 * Identity Core — public locator vocabulary for ETL v2 tours.
 *
 * Tour authors reference these aliases via `@plugin.identity-core:NAME`
 * (qualified) or `@:NAME` (when the running tour's `pluginId` is
 * `identity-core`). Renaming a testid below is a one-line change;
 * every tour referencing the alias keeps working.
 *
 * Naming convention: `<view>.<element>`. Use the kebab style of the
 * plugin's URL segments — keeps grep-ability across PRs.
 *
 * Authoring rules:
 * - Each value is a {@link Locator}: testid OR role+name OR label,
 *   not all three. Pick the most stable strategy for the element.
 * - Prefer ARIA (role+name / label) when the visible label is stable.
 * - Use testid when there's no semantic name OR when the label is
 *   localized (the testid is locale-independent).
 */

import type { TourLocatorVocabulary } from '@empowernow/etl-tour';

export const IDENTITY_CORE_LOCATORS: TourLocatorVocabulary = {
  /* ─── IdP Users list view ───────────────────────────────────────── */
  'idp-users.root':       { testid: 'identity-core-idp-users-root' },
  'idp-users.create-btn': { testid: 'identity-core-idp-users-create-btn' },
  'idp-users.bulk-bar':   { testid: 'identity-core-idp-users-bulk-bar' },
  // `<TextField data-testid="…">` hardcodes its own internal
  // `data-testid='text-field'` and drops the consumer prop. Until
  // TextField is fixed to forward, target the inner <input> by its
  // associated <label> text — the locator engine walks `for`/`id` to
  // resolve to the input itself.
  'idp-users.search':     { label: 'Search' },
  // `<DataTable data-testid="…">` likewise hardcodes
  // `'identity-core-datatable'` for every instance. We point at the
  // real testid that's emitted; per-view differentiation will need a
  // DataTable forwarding fix later.
  'idp-users.table':      { testid: 'identity-core-datatable' },

  /* ─── Datatable building-block (shared component) ───────────────── */
  'datatable.root':         { testid: 'identity-core-datatable' },
  'datatable.select-all':   { testid: 'identity-core-datatable-select-all' },
  'datatable.row':          { testid: 'identity-core-datatable-row' },

  /* ─── Identity dashboard ─────────────────────────────────────────── */
  'dashboard.root':         { testid: 'identity-core-dashboard' },
  'dashboard.search':       { testid: 'identity-core-dashboard-search' },
  'dashboard.search-btn':   { testid: 'identity-core-dashboard-search-btn' },
  'dashboard.quick-actions':{ testid: 'identity-core-dashboard-quick-actions' },

  /* ─── Create-auth-account wizard fields (form labels are stable) ── */
  'create-auth.first-name': { label: 'First Name' },
  'create-auth.last-name':  { label: 'Last Name' },
  'create-auth.email':      { label: 'Email' },
  'create-auth.submit':     { role: 'button', name: 'Create Account', exact: false },

  /* ─── Pagination + filters (shared) ─────────────────────────────── */
  'pagination.root': { testid: 'identity-core-pagination' },
  'pagination.prev': { testid: 'identity-core-pagination-prev' },
  'pagination.next': { testid: 'identity-core-pagination-next' },
  'filters.panel':   { testid: 'identity-core-filters-panel' },
  'filters.clear':   { testid: 'identity-core-filters-clear' },
};
